import {
    HttpTransportType,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from "@microsoft/signalr";
import { deviceEventTypes, socketEventStatus } from "../constants/SingnalRConstant";

/* Local module variable — one socket per HubSpot co-browse iframe page. */
let connection = null;

/* Serializes Connect/Disconnect so they never interleave (see Connect). */
let opChain = Promise.resolve();

/* Drop a device with no event for 30s (matches the Zendesk app); sweep every 2s. */
const STALE_MS = 30000;
const CLEANUP_MS = 2000;

/* Retry the INITIAL socket connect — an idle/cold socket often drops the first
   WebSocket upgrade, and withAutomaticReconnect doesn't cover the initial start. */
const CONNECT_ATTEMPTS = 3;
const CONNECT_RETRY_MS = 1200;

/* The socket group is the literal string "domain" — same as the visitor SDK
   (blitzzcobrowse.js `GroupName="domain"`) and the app-wide services/signalR.js.
   The server scopes "domain" to a single tenant by the connection's access_token
   (the token carries the domain/CID), so the group NAME is constant across tenants;
   the token is what isolates them. */
const GroupName = "domain";

/* Decode a JWT payload (base64url) without verifying — we only read its claims. */
const decodeJwtPayload = (token) => {
    try {
        let payload = (token || "").split(".")[1] || "";
        payload = payload.replace(/-/g, "+").replace(/_/g, "/");
        while (payload.length % 4) payload += "=";
        return JSON.parse(decodeURIComponent(escape(atob(payload))));
    } catch (error) {
        return null;
    }
};

/*
    Inspect the URL token up front (before opening a socket) so the iframe view can tell
    the agent the sign-in has expired instead of spinning to a misleading "Visitor is
    offline": `expired` is true when the JWT `exp` claim is in the past.
    `exp` is seconds since epoch; absent/garbage `exp` is treated as not-expired (we can't
    prove expiry, so we don't block on it).
*/
export const inspectToken = (token) => {
    const decoded = decodeJwtPayload(token);
    const exp = decoded && Number(decoded.exp);
    const expired = Number.isFinite(exp) && exp * 1000 <= Date.now();
    return { expired };
};

/*
    SignalR client for the HubSpot co-browse iframe view. Kept SEPARATE from the
    app-wide services/signalR.js, which authenticates with the logged-in agent's
    stored token and joins the static "domain" group. This view runs inside a
    HubSpot iframe where there is NO logged-in agent: the token arrives on the URL
    (and scopes the connection's tenant). It tracks visitor device heartbeats and
    notifies the caller whenever the device list changes.
*/
class HubSpotCobrowseSignalR {
    token = null;
    devices = new Map(); // sessionID/device_id → device payload (+ lastSeenAt)
    cleanupTimer = null;
    onDevicesChanged = null; // (devices: Array) => void

    /*
        Open the socket, join the domain group, and start tracking devices.
        `onDevicesChanged` fires with the current device array on every change.
        Rejects if the socket fails to start so the caller can show an error.

        Connect and Disconnect are serialized through `opChain` so a teardown can never
        interleave with a setup — e.g. "Try again" re-runs the effect (cleanup Disconnect
        + new Connect) while a previous Disconnect's connection.stop() is still in flight.
        Without serialization, the lagging Disconnect could .off()/null the freshly-built
        connection (or null onDevicesChanged), breaking the reconnect. Chaining guarantees
        the previous op fully finishes before the next starts.
    */
    Connect = (args) => {
        const result = opChain.then(
            () => this._connect(args),
            () => this._connect(args) // run even if the previous op rejected
        );
        opChain = result.catch(() => {}); // keep the chain alive on failure
        return result; // caller still sees the real success/failure
    };

    _connect = async ({ token, socketUrl, onDevicesChanged }) => {
        // By the time this runs, any prior Disconnect in the chain has finished, so
        // connection is null. The guard is a belt-and-suspenders no-op.
        if (connection) await this._disconnect();

        this.token = token;            // set AFTER _disconnect (which nulls it)
        this.onDevicesChanged = onDevicesChanged;

        // An idle/cold socket (e.g. a dev instance scaling from zero) often drops the
        // FIRST WebSocket upgrade — seen in the Network tab as a fast "Finished"/0-byte
        // close — and a reload then connects fine. `withAutomaticReconnect` only covers
        // drops AFTER a successful start, not the initial connect, so we retry the
        // initial connect a few times before surfacing the retryable connectError.
        let lastError;
        for (let attempt = 1; attempt <= CONNECT_ATTEMPTS; attempt++) {
            this.DefineConnection(socketUrl);
            connection.on("SendMessage", this.ReceiveMessage);
            connection.onreconnected(() => this.JoinGroup());
            try {
                await connection.start();
                await this.JoinGroup();
                this.StartStaleCleanup();
                this.Notify();
                return;
            } catch (error) {
                lastError = error;
                console.warn(`[Cobrowse] connect attempt ${attempt}/${CONNECT_ATTEMPTS} failed:`, error?.message);
                // Tear down the dead connection but KEEP token/onDevicesChanged for the retry.
                if (connection) {
                    try { await connection.stop(); } catch (e) { /* already closed */ }
                    connection.off();
                    connection = null;
                }
                if (attempt < CONNECT_ATTEMPTS) {
                    await new Promise((resolve) => setTimeout(resolve, CONNECT_RETRY_MS));
                }
            }
        }
        console.error("[Cobrowse] Socket connect failed after retries:", lastError?.message);
        throw lastError;
    };

    DefineConnection = (socketUrl) => {
        /* Same builder config as services/signalR.js, but the token is supplied
           by the caller (from the URL) instead of read from storage. */
        connection = new HubConnectionBuilder()
            .withUrl(socketUrl, {
                accessTokenFactory: () => this.token,
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();
    };

    JoinGroup = async () => {
        try {
            if (connection && connection.state === HubConnectionState.Connected) {
                await connection.invoke("JoinGroup", GroupName);
            }
        } catch (error) {
            console.warn("[Cobrowse] JoinGroup failed:", error?.message);
        }
    };

    /* Handle device-heartbeat events (same shape as the Zendesk app). */
    ReceiveMessage = (user, message) => {
        let parsed;
        try {
            parsed = typeof message === "string" ? JSON.parse(message) : message;
        } catch (error) {
            return;
        }
        if (!parsed) return;

        if (parsed.event) {
            switch (parsed.event) {
                case deviceEventTypes.ReplaySession:
                case deviceEventTypes.DeviceInfo:
                case deviceEventTypes.ReferenceUpdated:
                    this.UpsertDevice(parsed.data || {});
                    break;
                default:
                    break;
            }
        } else if (parsed.eventType === socketEventStatus.Disconnected) {
            this.RemoveDevice(
                parsed.code ||
                    parsed.sessionID ||
                    parsed.device_id ||
                    (parsed.data && (parsed.data.code || parsed.data.sessionID || parsed.data.device_id))
            );
        }
    };

    UpsertDevice = (data) => {
        const key = data.sessionID || data.device_id;
        if (!key) return;
        const existing = this.devices.get(key) || {};
        this.devices.set(key, {
            sessionID: data.sessionID || existing.sessionID,
            device_id: data.device_id || existing.device_id,
            reference: data.reference != null ? data.reference : existing.reference,
            deviceInfo: data.deviceInfo || existing.deviceInfo || {},
            hubspotConversationId:
                data.hubspotConversationId != null
                    ? data.hubspotConversationId
                    : existing.hubspotConversationId,
            lastSeenAt: Date.now(),
        });
        this.Notify();
    };

    RemoveDevice = (key) => {
        if (key && this.devices.has(key)) {
            this.devices.delete(key);
            this.Notify();
        }
    };

    /* Stale cleanup in case a 'disconnected' event is missed. */
    StartStaleCleanup = () => {
        this.cleanupTimer = setInterval(() => {
            const now = Date.now();
            let changed = false;
            this.devices.forEach((device, key) => {
                if (now - device.lastSeenAt > STALE_MS) {
                    this.devices.delete(key);
                    changed = true;
                }
            });
            if (changed) this.Notify();
        }, CLEANUP_MS);
    };

    Notify = () => {
        if (this.onDevicesChanged) {
            this.onDevicesChanged(Array.from(this.devices.values()));
        }
    };

    /* Stop the socket and reset all state — call on iframe/modal teardown.
       Serialized through opChain (see Connect) so it can't race a setup. */
    Disconnect = () => {
        const result = opChain.then(
            () => this._disconnect(),
            () => this._disconnect()
        );
        opChain = result.catch(() => {});
        return result;
    };

    _disconnect = async () => {
        if (this.cleanupTimer) {
            clearInterval(this.cleanupTimer);
            this.cleanupTimer = null;
        }
        if (connection) {
            try {
                await connection.stop();
            } catch (error) {
                console.error("[Cobrowse] Error while stopping connection:", error?.message);
            }
            connection.off();
            connection = null;
        }
        this.devices.clear();
        this.onDevicesChanged = null;
        this.token = null;
    };
}

export default new HubSpotCobrowseSignalR();
